import type { Classification } from "../../types/projects";

/** Files-tab classification badge — one per layer row. */
export function ClassificationBadge({
  classification,
  confidence,
}: {
  classification: Classification | null | undefined;
  confidence: number | null | undefined;
}) {
  if (!classification) {
    return (
      <span
        data-testid="classification-badge-pending"
        title="No classification — file is geometry-only or pending."
        style={badgeStyle("#e5e7eb", "#4b5563")}
      >
        —
      </span>
    );
  }
  const { icon, label, color, fg } = renderFor(classification);
  const pct =
    typeof confidence === "number"
      ? ` (${Math.round(confidence * 100)}%)`
      : "";
  return (
    <span
      data-testid={`classification-badge-${classification}`}
      title={`${label}${pct}`}
      style={badgeStyle(color, fg)}
    >
      {icon} {label}
      {pct}
    </span>
  );
}

function renderFor(classification: Classification) {
  switch (classification) {
    case "module":
      return {
        icon: "📄",
        label: "Module datasheet",
        color: "#dbeafe",
        fg: "#1e40af",
      };
    case "inverter":
      return {
        icon: "📄",
        label: "Inverter datasheet",
        color: "#dcfce7",
        fg: "#166534",
      };
    case "tracker":
      return {
        icon: "📄",
        label: "Tracker datasheet",
        color: "#fef3c7",
        fg: "#92400e",
      };
    case "other":
      return { icon: "❓", label: "Other", color: "#f3f4f6", fg: "#4b5563" };
    case "unknown":
      return {
        icon: "❓",
        label: "Unknown",
        color: "#f3f4f6",
        fg: "#4b5563",
      };
  }
}

/** Boundary / KMZ / GeoJSON badge for non-PDF layer types. */
export function GeometryBadge({ layerType }: { layerType: string }) {
  const label =
    layerType === "boundary"
      ? "🗺️ Boundary"
      : layerType === "exclusion_zone"
        ? "🗺️ Exclusion zone"
        : layerType === "road"
          ? "🗺️ Road"
          : layerType === "point_of_interest"
            ? "🗺️ POI"
            : "🗺️ Geometry";
  return (
    <span
      data-testid={`geometry-badge-${layerType}`}
      style={badgeStyle("#ede9fe", "#5b21b6")}
    >
      {label}
    </span>
  );
}

function badgeStyle(bg: string, fg: string): React.CSSProperties {
  return {
    display: "inline-block",
    padding: "0.15rem 0.5rem",
    borderRadius: "999px",
    backgroundColor: bg,
    color: fg,
    fontSize: "0.75rem",
    fontWeight: 500,
  };
}
